import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Calendar, ChefHat, Filter, Star, Clock, Utensils, TrendingUp, LogOut, Shield, Eye, EyeOff, Download, Wifi, WifiOff, Bell, AlertTriangle, User, Settings } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Signup from './components/Signup';
import EmailVerification from './components/EmailVerification';
import AccountSettings from './components/AccountSettings';

import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001/api';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Main Meal Management Component (your existing functionality)
const MealManagement = () => {
  const { user, logout, isAdmin } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');
  const [meals, setMeals] = useState([]);
  const [recentMeals, setRecentMeals] = useState([]);
  const [targetStarch, setTargetStarch] = useState('');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    mealType: '',
    starch: '',
    minStars: '',
    search: ''
  });

  // PWA States
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [offlineActions, setOfflineActions] = useState([]);

  // Token Management States
  const [tokenStatus, setTokenStatus] = useState({ valid: true, expiresIn: 3600 });
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // User menu state
  const [showUserMenu, setShowUserMenu] = useState(false);

  // API call function with authentication
  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE}${endpoint}`;
    const token = localStorage.getItem('token');
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `API Error: ${response.statusText}`);
      }
      
      // Cache recent meals for offline use
      if (endpoint.includes('/meals/recent')) {
        localStorage.setItem('cachedMeals', JSON.stringify(data.meals || []));
      }
      
      return data;
    } catch (error) {
      if (!isOnline) {
        if (endpoint.includes('/meals/recent')) {
          const cachedMeals = JSON.parse(localStorage.getItem('cachedMeals') || '[]');
          return { meals: cachedMeals, offline: true };
        }
        
        if (options.method && options.method !== 'GET') {
          const action = { url, options, timestamp: Date.now(), type: endpoint };
          const existing = JSON.parse(localStorage.getItem('offlineActions') || '[]');
          localStorage.setItem('offlineActions', JSON.stringify([...existing, action]));
          setOfflineActions(prev => [...prev, action]);
          
          throw new Error('Action queued for when online');
        }
      }
      throw error;
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching initial data...');
      
      const [recentData, targetData, statsData] = await Promise.all([
        apiCall('/meals/recent/10').catch((error) => {
          console.error('Failed to fetch recent meals:', error);
          return { meals: [] };
        }),
        apiCall('/meals/target-starch').catch((error) => {
          console.error('Failed to fetch target starch:', error);
          return { targetStarch: 'Unknown' };
        }),
        apiCall('/meals/stats/overview').catch((error) => {
          console.error('Failed to fetch stats:', error);
          return {};
        })
      ]);
      
      setRecentMeals(recentData.meals || []);
      setTargetStarch(targetData.targetStarch || 'Unknown');
      setStats(statsData);
      
    } catch (error) {
      console.error('❌ Failed to fetch initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setNextMeal = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/meals/set-next', {
        method: 'POST',
        body: JSON.stringify({ numdays: 10 })
      });
      
      setRecentMeals(response.recentMeals || []);
      await fetchInitialData();
      
      if (isOnline) {
        alert('Next meal assigned successfully!');
      } else {
        alert('Meal queued - will sync when online!');
      }
    } catch (error) {
      alert('Failed to set next meal: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeals = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await apiCall(`/meals?${queryParams}`);
      setMeals(response.meals || []);
    } catch (error) {
      alert('Failed to fetch meals: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStarDisplay = (stars) => {
    const starCount = parseInt(stars) || 0;
    
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        size={16} 
        className={i < starCount ? 'text-yellow-500' : 'text-gray-300'}
        style={i < starCount ? { fill: '#eab308', color: '#eab308' } : {}}
      />
    ));
  };

  // Load data when component mounts
  useEffect(() => {
    fetchInitialData();

    // Setup online/offline detection
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div 
      className="min-h-screen pb-20 md:pb-0"
      style={{
        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)',
        backgroundImage: `
          radial-gradient(circle at 25% 25%, rgba(34, 197, 94, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.1) 0%, transparent 50%)
        `
      }}
    >
      {/* PWA Status Bar */}
      <div className="bg-green-600 text-white p-2 text-center text-sm flex items-center justify-center space-x-4">
        <div className="flex items-center">
          {isOnline ? (
            <>
              <Wifi size={16} className="mr-1" />
              <span>Online</span>
            </>
          ) : (
            <>
              <WifiOff size={16} className="mr-1" />
              <span>Offline Mode</span>
            </>
          )}
        </div>
        
        {offlineActions.length > 0 && (
          <div className="flex items-center">
            <Clock size={16} className="mr-1" />
            <span>{offlineActions.length} actions queued</span>
          </div>
        )}

        <div className="flex items-center text-xs">
          <span>Welcome, {user?.username}</span>
          {isAdmin() && <span className="ml-2 px-2 py-1 bg-red-500 rounded text-xs">Admin</span>}
        </div>
      </div>
      
      {/* Enhanced Header */}
      <header className="bg-white bg-opacity-95 backdrop-blur-md shadow-xl border-b border-green-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <ChefHat className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Meal Management System
                </h1>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-green-600 font-medium">Target Starch:</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                    {targetStarch}
                  </span>
                </div>
              </div>
            </div>

            <nav className="hidden md:flex space-x-2">
              <button
                onClick={() => setCurrentView('dashboard')}
                className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center space-x-2 ${
                  currentView === 'dashboard' 
                    ? 'bg-green-500 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                }`}
              >
                <TrendingUp size={18} />
                <span className="font-medium">Dashboard</span>
              </button>
              <button
                onClick={() => { setCurrentView('meals'); fetchMeals(); }}
                className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center space-x-2 ${
                  currentView === 'meals' 
                    ? 'bg-green-500 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                }`}
              >
                <Utensils size={18} />
                <span className="font-medium">All Meals</span>
              </button>
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-green-50 transition-colors"
                >
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-700">{user?.username}</p>
                    <p className="text-xs text-green-600">
                      {user?.role === 'admin' ? 'Administrator' : 'User'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <User size={20} className="text-green-600" />
                  </div>
                </button>

                {/* User Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="font-medium text-gray-800">{user?.username}</p>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                    
                    <button
                      onClick={() => {
                        window.location.href = '/account';
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                    >
                      <Settings size={16} className="text-gray-400" />
                      <span className="text-sm">Account Settings</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-red-50 transition-colors flex items-center space-x-2 border-t border-gray-100 mt-2 pt-2"
                    >
                      <LogOut size={16} className="text-red-500" />
                      <span className="text-sm text-red-600 font-medium">Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentView === 'dashboard' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white bg-opacity-80 p-6 rounded-xl shadow-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Meals</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.totalMeals || 0}</p>
                  </div>
                  <Utensils className="text-green-500" size={32} />
                </div>
              </div>
              
              <div className="bg-white bg-opacity-80 p-6 rounded-xl shadow-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg Rating</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.averageStars || '0.0'}</p>
                  </div>
                  <Star className="text-yellow-500" size={32} />
                </div>
              </div>

              <div className="bg-white bg-opacity-80 p-6 rounded-xl shadow-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Target Starch</p>
                    <p className="text-lg font-bold text-green-600">{targetStarch}</p>
                  </div>
                  <Filter className="text-green-500" size={32} />
                </div>
              </div>

              <div className="bg-white bg-opacity-80 p-6 rounded-xl shadow-lg border border-green-200">
                <button
                  onClick={setNextMeal}
                  disabled={loading}
                  className="w-full h-full flex flex-col items-center justify-center space-y-2 text-white bg-green-500 hover:bg-green-600 rounded-xl transition-colors disabled:opacity-50 relative"
                >
                  <Clock size={32} />
                  <span className="font-semibold">
                    {loading ? 'Setting...' : 'Set Next Meal'}
                  </span>
                  {!isOnline && (
                    <span className="absolute -top-2 -right-2 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
                      <WifiOff size={10} className="text-white" />
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Recent Meals Table */}
            <div className="bg-white bg-opacity-80 rounded-xl shadow-lg border border-green-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-green-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Calendar className="mr-2 text-green-500" size={20} />
                  Recent Meal Schedule
                </h2>
                {!isOnline && recentMeals.length > 0 && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium flex items-center">
                    <WifiOff size={14} className="mr-1" />
                    Offline Data
                  </span>
                )}
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Serve Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Meal Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Starch
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rating
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {recentMeals.map((meal, index) => (
                      <tr key={meal.id} className={index === 0 ? 'bg-green-50' : 'hover:bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(meal.serve_date).toLocaleDateString()}
                          {index === 0 && <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Next</span>}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {meal.meal_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            meal.starch === 'Pasta' ? 'bg-yellow-100 text-yellow-800' :
                            meal.starch === 'Rice' ? 'bg-blue-100 text-blue-800' :
                            meal.starch === 'Potato' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {meal.starch}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex">
                            {getStarDisplay(meal.stars)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          #{meal.id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Statistics Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white bg-opacity-80 p-6 rounded-xl shadow-lg border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Meal Types</h3>
                <div className="space-y-3">
                  {stats.mealTypes?.map((type) => (
                    <div key={type.MEAL_TYPE} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{type.MEAL_TYPE}</span>
                      <span className="text-sm font-semibold text-gray-800">{type.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white bg-opacity-80 p-6 rounded-xl shadow-lg border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Starch Distribution</h3>
                <div className="space-y-3">
                  {stats.starchTypes?.map((starch) => (
                    <div key={starch.STARCH} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{starch.STARCH}</span>
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${
                          starch.STARCH === targetStarch ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className="text-sm font-semibold text-gray-800">{starch.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentView === 'meals' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white bg-opacity-80 p-6 rounded-xl shadow-lg border border-green-200">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <Filter className="mr-2 text-green-500" size={20} />
                Filter Meals
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
                  <select
                    value={filters.mealType}
                    onChange={(e) => setFilters({...filters, mealType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">All Types</option>
                    <option value="Breakfast">Breakfast</option>
                    <option value="Dinner">Dinner</option>
                    <option value="Dessert">Dessert</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Starch Type</label>
                  <select
                    value={filters.starch}
                    onChange={(e) => setFilters({...filters, starch: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">All Starches</option>
                    <option value="Pasta">Pasta</option>
                    <option value="Rice">Rice</option>
                    <option value="Potato">Potato</option>
                    <option value="NoStarch">No Starch</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Stars</label>
                  <select
                    value={filters.minStars}
                    onChange={(e) => setFilters({...filters, minStars: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Any Rating</option>
                    <option value="1">1+ Stars</option>
                    <option value="2">2+ Stars</option>
                    <option value="3">3+ Stars</option>
                    <option value="4">4+ Stars</option>
                    <option value="5">5 Stars</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    placeholder="Search meal names..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <button
                onClick={fetchMeals}
                disabled={loading}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                {loading ? 'Searching...' : 'Apply Filters'}
              </button>
            </div>

            {/* All Meals Table */}
            <div className="bg-white bg-opacity-80 rounded-xl shadow-lg border border-green-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-green-200">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Utensils className="mr-2 text-green-500" size={20} />
                  All Meals ({meals.length})
                </h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Meal Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Starch
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rating
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Served
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Protein
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {meals.map((meal) => (
                      <tr key={meal.MEAL_ID} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          #{meal.MEAL_ID}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                          <div className="font-medium">{meal.MEAL_NAME}</div>
                          {meal.MEAL_NOTES && (
                            <div className="text-gray-500 text-xs mt-1 truncate">{meal.MEAL_NOTES}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            meal.MEAL_TYPE === 'Breakfast' ? 'bg-orange-100 text-orange-800' :
                            meal.MEAL_TYPE === 'Dinner' ? 'bg-blue-100 text-blue-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {meal.MEAL_TYPE}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            meal.STARCH === 'Pasta' ? 'bg-yellow-100 text-yellow-800' :
                            meal.STARCH === 'Rice' ? 'bg-blue-100 text-blue-800' :
                            meal.STARCH === 'Potato' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {meal.STARCH}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex">
                            {getStarDisplay(meal.NOOFSTARS)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {meal.MEAL_LASTSERVED ? new Date(meal.MEAL_LASTSERVED).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {meal.PROTEIN || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {meals.length === 0 && !loading && (
                <div className="px-6 py-8 text-center text-gray-500">
                  No meals found. Try adjusting your filters.
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Enhanced Mobile Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white bg-opacity-95 backdrop-blur-md border-t border-green-200 px-4 py-2 shadow-lg">
        <div className="flex justify-around">
          <button
            onClick={() => setCurrentView('dashboard')}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-all duration-200 ${
              currentView === 'dashboard' ? 'text-green-600 bg-green-50' : 'text-gray-600'
            }`}
          >
            <TrendingUp size={20} />
            <span className="text-xs mt-1">Dashboard</span>
          </button>
          <button
            onClick={() => { setCurrentView('meals'); fetchMeals(); }}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-all duration-200 ${
              currentView === 'meals' ? 'text-green-600 bg-green-50' : 'text-gray-600'
            }`}
          >
            <Utensils size={20} />
            <span className="text-xs mt-1">Meals</span>
          </button>
          <button
            onClick={setNextMeal}
            disabled={loading}
            className="flex flex-col items-center py-2 px-3 rounded-lg text-green-600 disabled:opacity-50 relative"
          >
            <div className="relative">
              <Clock size={20} />
              {!isOnline && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full"></span>
              )}
            </div>
            <span className="text-xs mt-1">Next Meal</span>
          </button>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex flex-col items-center py-2 px-3 rounded-lg text-gray-600 relative"
          >
            <User size={20} />
            <span className="text-xs mt-1">Account</span>
            {/* Mobile user menu */}
            {showUserMenu && (
              <div className="absolute bottom-full right-0 mb-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="font-medium text-gray-800">{user?.username}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                  <p className="text-xs text-green-600 mt-1">
                    {user?.role === 'admin' ? 'Administrator' : 'User'}
                  </p>
                </div>
                
                <button
                  onClick={() => {
                    window.location.href = '/account';
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors flex items-center space-x-2"
                >
                  <Settings size={16} className="text-gray-400" />
                  <span className="text-sm">Account Settings</span>
                </button>
                
                <button
                  onClick={() => {
                    logout();
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-red-50 transition-colors flex items-center space-x-2 border-t border-gray-100 mt-2 pt-2"
                >
                  <LogOut size={16} className="text-red-500" />
                  <span className="text-sm text-red-600 font-medium">Sign Out</span>
                </button>
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main App Component with Routing
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<EmailVerification />} />

            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MealManagement />
              </ProtectedRoute>
            } />
            
            <Route path="/account" element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            } />
            
            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" />} />
            
            {/* Catch all route - redirect to login */}
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
